import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  register,
  registrationErrorMessage,
} from "@shared/api/candidateApi.ts";
import { ApiError } from "@shared/api/http.ts";
import { Alert, Button } from "@shared/components/Button.tsx";
import { CandidateFields } from "@shared/components/CandidateFields.tsx";
import { CandidateIdBox } from "@shared/components/CandidateIdBox.tsx";
import {
  Turnstile,
  type TurnstileHandle,
} from "@shared/components/Turnstile.tsx";
import { Icon, type IconName } from "@shared/components/Icon.tsx";
import { Markdown } from "@shared/components/Markdown.tsx";
import {
  type CandidateFormState,
  emptyCandidateForm,
  type FormErrors,
  gradeLabel,
  normalizeMobile,
  toRegisterRequest,
  withGroup,
  validateCandidate,
} from "@shared/lib/candidateForm.ts";
import {
  collegeLevelLabel,
  GROUPS,
  type Group,
  districtLabel,
  genderLabel,
  groupLabel,
  languageLabel,
  qualificationLabel,
  stateLabel,
  typeLabel,
} from "@shared/lib/enums.ts";
import { formatDateMr, toDeva } from "@shared/lib/format.ts";
import type { CandidateType } from "@shared/types.ts";
import { COMPETITION } from "../content/competition.ts";
import { useLegalDoc } from "../useLegalDoc.ts";
import { LegalDocSkeleton } from "./Rules.tsx";
import { PageBanner } from "../Layout.tsx";
import { ErrorPage } from "./ErrorPage.tsx";

/** 'finish' = review everything & submit. Registration is free — there is no payment step. */
type Step = "terms" | "category" | "details" | "finish" | "success" | "blocked";
const STEPS: { key: Exclude<Step, "success" | "blocked">; label: string }[] = [
  { key: "terms", label: "माहिती, नियम आणि अटी" },
  { key: "category", label: "गट निवड" },
  { key: "details", label: "माहिती" },
  { key: "finish", label: "पुष्टी" },
];
const GROUP_EMOJI: Record<Group, string> = { school: "🎒", open: "🎓" };

/* ------------------------- draft (survives a refresh) -------------------- */
const DRAFT_KEY = "shivavakta.registration.draft";
interface Draft {
  form: CandidateFormState;
  accepted: boolean;
  step: Step;
}
function loadDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}
function saveDraft(d: Draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* storage unavailable — fine */
  }
}
function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

/* ------------- rate-limit block (429) — survives a refresh ---------------- */
const BLOCK_KEY = "shivavakta.registration.blockedUntil";
/** Fallback only — the backend always sends Retry-After with 429 (its windows are 1 hour). */
const DEFAULT_BLOCK_SEC = 60 * 60;

function loadBlockedUntil(): number | null {
  try {
    const v = Number(sessionStorage.getItem(BLOCK_KEY));
    return v > Date.now() ? v : null;
  } catch {
    return null;
  }
}
function saveBlockedUntil(t: number | null) {
  try {
    if (t) sessionStorage.setItem(BLOCK_KEY, String(t));
    else sessionStorage.removeItem(BLOCK_KEY);
  } catch {
    /* ignore */
  }
}

function focusFirstError() {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(
      ".has-error input, .has-error select, .has-error textarea",
    );
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus({ preventScroll: true });
  });
}

/* ================================ page ==================================== */

export function RegisterPage() {
  const draft = useRef(loadDraft()).current;
  const restored = draft?.step === ("payment" as Step) ? "finish" : draft?.step; // older drafts
  const [blockedUntil, setBlockedUntil] = useState<number | null>(
    loadBlockedUntil,
  );
  const [step, setStep] = useState<Step>(() =>
    blockedUntil
      ? "blocked"
      : restored && restored !== "success" && restored !== "blocked"
        ? restored
        : "terms",
  );
  const [accepted, setAccepted] = useState(draft?.accepted ?? false);
  const [form, setForm] = useState<CandidateFormState>(() =>
    draft ? { ...emptyCandidateForm(), ...draft.form } : emptyCandidateForm(),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [result, setResult] = useState<{ id: string; at: Date } | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  /** Turnstile token for the NEXT submission — never stored, never reused. */
  const [botToken, setBotToken] = useState<string | null>(null);
  const botRef = useRef<TurnstileHandle>(null);
  /**
   * Synchronous single-flight lock. `submitting` state only disables the button after the next
   * render, so a fast double-click (or a laggy device) could otherwise fire two requests.
   * Released only when the attempt fails; after success the page moves on and stays locked.
   */
  const submitLock = useRef(false);

  useEffect(() => {
    if (step === "success") return;
    // A blocked candidate resumes at the last step, with everything still filled in.
    saveDraft({ form, accepted, step: step === "blocked" ? "finish" : step });
  }, [form, accepted, step]);

  const go = (s: Step) => {
    setStep(s);
    setSubmitError(undefined);
    // Any step change discards the token; the final step mounts a fresh widget.
    setBotToken(null);
    requestAnimationFrame(() =>
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  const continueFromDetails = () => {
    const e = validateCandidate(form, "register");
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError();
    go("finish");
  };

  const submit = async () => {
    if (!botToken || submitLock.current) return;
    submitLock.current = true;
    // Re-check everything in case a restored draft is stale.
    const all = validateCandidate(form, "register");
    if (Object.keys(all).length) {
      submitLock.current = false;
      setErrors(all);
      go("details");
      return;
    }
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const { id } = await register(toRegisterRequest(form, botToken));
      setResult({ id, at: new Date() });
      clearDraft();
      go("success");
    } catch (err) {
      submitLock.current = false;
      if (err instanceof ApiError && err.status === 429) {
        const until =
          Date.now() + (err.retryAfterSec ?? DEFAULT_BLOCK_SEC) * 1000;
        setBlockedUntil(until);
        saveBlockedUntil(until);
        go("blocked");
      } else {
        setSubmitError(registrationErrorMessage(err));
        // Tokens are single-use: always get a fresh one before the next attempt.
        botRef.current?.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const retryAfterBlock = () => {
    setBlockedUntil(null);
    saveBlockedUntil(null);
    go("finish");
  };

  const startOver = () => {
    clearDraft();
    submitLock.current = false;
    setForm(emptyCandidateForm());
    setAccepted(false);
    setErrors({});
    setResult(null);
    go("terms");
  };

  return (
    <>
      <PageBanner title={`${COMPETITION.name} — नोंदणी`} />
      <div className="wizard" ref={topRef}>
        {step === "blocked" && (
          <RateLimited
            until={blockedUntil ?? Date.now()}
            onRetry={retryAfterBlock}
          />
        )}

        {step !== "success" && step !== "blocked" && <Stepper current={step} />}

        {step === "terms" && (
          <TermsStep
            accepted={accepted}
            setAccepted={setAccepted}
            onNext={() => go("category")}
          />
        )}

        {step === "category" && (
          <StepCard
            title="आपला गट निवडा"
            back={() => go("terms")}
            next={() => go("details")}
            nextDisabled={!form.group}
          >
            <div
              className="category-grid category-grid--two"
              role="radiogroup"
              aria-label="गट"
            >
              {GROUPS.map((g) => {
                const on = form.group === g.value;
                return (
                  <button
                    key={g.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`category-card ${on ? "is-selected" : ""}`}
                    onClick={() => setForm(withGroup(form, g.value))}
                  >
                    <div className="category-card__icon" aria-hidden="true">
                      {GROUP_EMOJI[g.value]}
                    </div>
                    <div className="category-card__title">{g.label}</div>
                    <div className="category-card__desc">{g.desc}</div>
                    <div className="category-card__eligibility">
                      {g.eligibility}
                    </div>
                    {on && (
                      <span className="category-card__tick">
                        <Icon name="check" size={16} /> निवडले
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </StepCard>
        )}

        {step === "details" && (
          <StepCard
            title="स्पर्धकाची माहिती"
            sub={
              <>
                गट: <strong>{groupLabel(form.group)}</strong> ·{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => go("category")}
                >
                  बदला
                </button>
              </>
            }
            back={() => go("category")}
            next={continueFromDetails}
          >
            {Object.keys(errors).length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <Alert tone="error" title="काही माहिती अपूर्ण किंवा चुकीची आहे">
                  लाल रंगात दाखवलेली माहिती दुरुस्त करा.
                </Alert>
              </div>
            )}
            <CandidateFields
              value={form}
              onChange={(f) => {
                setForm(f);
                if (Object.keys(errors).length)
                  setErrors(validateCandidate(f, "register"));
              }}
              errors={errors}
              mode="register"
              idPrefix="reg"
            />
          </StepCard>
        )}

        {step === "finish" && (
          <StepCard
            title="माहिती तपासा व नोंदणी पूर्ण करा"
            sub="खालील माहिती तपासा आणि नोंदणी पूर्ण करा."
            back={() => go("details")}
            next={submit}
            nextLabel="नोंदणी पूर्ण करा"
            nextIcon="check"
            loading={submitting}
            loadingText="नोंदणी होत आहे…"
            nextDisabled={!botToken}
          >
            <ReviewSummary form={form} onEdit={() => go("details")} open />
            <Turnstile ref={botRef} onToken={setBotToken} />
            {!botToken && !submitting && (
              <p className="bot-check__hint">
                नोंदणी पूर्ण करण्यासाठी वरील सुरक्षा तपासणी पूर्ण करा.
              </p>
            )}
            {submitError && (
              <div style={{ marginTop: 20 }}>
                <Alert tone="error" title="नोंदणी पूर्ण होऊ शकली नाही">
                  {submitError}
                </Alert>
              </div>
            )}
          </StepCard>
        )}

        {step === "success" && result && (
          <Success
            id={result.id}
            at={result.at}
            name={form.name}
            type={form.candidate_type as CandidateType}
            onAnother={startOver}
          />
        )}
      </div>
    </>
  );
}

/* =============================== pieces =================================== */

function Stepper({ current }: { current: Step }) {
  const steps = STEPS;
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <nav aria-label="नोंदणीच्या पायऱ्या">
      <ol className="progress">
        {steps.map((s, i) => (
          <li
            key={s.key}
            className={i < idx ? "is-done" : i === idx ? "is-current" : ""}
            aria-current={i === idx ? "step" : undefined}
          >
            <span className="progress__dot">
              {i < idx ? <Icon name="check" size={14} /> : toDeva(i + 1)}
            </span>
            <span className="progress__label">{s.label}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function StepCard({
  title,
  sub,
  children,
  back,
  next,
  nextLabel = "पुढे जा",
  nextIcon = "arrowRight",
  nextDisabled,
  loading,
  loadingText,
}: {
  title: string;
  sub?: ReactNode;
  children: ReactNode;
  back?: () => void;
  next: () => void;
  nextLabel?: string;
  nextIcon?: IconName;
  nextDisabled?: boolean;
  loading?: boolean;
  loadingText?: string;
}) {
  return (
    <section className="step">
      <h2>{title}</h2>
      {sub && <p className="step-sub">{sub}</p>}
      <div>{children}</div>
      <footer className="step-actions">
        {back ? (
          <Button
            variant="secondary"
            icon="arrowLeft"
            onClick={back}
            disabled={loading}
          >
            मागे
          </Button>
        ) : (
          <span />
        )}
        <Button
          size="lg"
          iconRight={nextIcon}
          onClick={next}
          disabled={nextDisabled}
          loading={loading}
          loadingText={loadingText}
        >
          {nextLabel}
        </Button>
      </footer>
    </section>
  );
}

function TermsStep({
  accepted,
  setAccepted,
  onNext,
}: {
  accepted: boolean;
  setAccepted: (v: boolean) => void;
  onNext: () => void;
}) {
  const doc = useLegalDoc();
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const checkScrolledToEnd = (el: HTMLDivElement) => {
    if (el.scrollHeight - (el.scrollTop + el.clientHeight) <= 2) {
      setScrolledToEnd(true);
    }
  };

  useEffect(() => {
    if (!doc.loading && boxRef.current) checkScrolledToEnd(boxRef.current);
  }, [doc.loading]);

  return (
    <StepCard
      title="माहिती, नियम आणि अटी"
      sub="पुढे जाण्यापूर्वी खालील माहिती, नियम आणि अटी पूर्ण वाचा."
      next={onNext}
      nextDisabled={!accepted || doc.loading || !scrolledToEnd}
    >
      {!doc.loading && doc.fallback && (
        <div style={{ marginBottom: 14 }}>
          <Alert
            tone="warn"
            title="अधिकृत मजकूर लोड होऊ शकला नाही"
            action={
              <Button
                size="sm"
                variant="secondary"
                icon="refresh"
                onClick={doc.retry}
              >
                पुन्हा
              </Button>
            }
          >
            तात्पुरता मजकूर दाखवला आहे.
          </Alert>
        </div>
      )}
      <div
        ref={boxRef}
        className="doc-box"
        tabIndex={0}
        aria-label="माहिती, नियम आणि अटी"
        onScroll={(e) => checkScrolledToEnd(e.currentTarget)}
      >
        {doc.loading ? <LegalDocSkeleton /> : <Markdown source={doc.content} />}
      </div>
      <label className="check-row" htmlFor="reg_accept">
        <input
          id="reg_accept"
          type="checkbox"
          checked={accepted}
          disabled={doc.loading || !scrolledToEnd}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        <span>
          मी वरील सर्व माहिती, नियम आणि अटी वाचल्या असून मला त्या मान्य आहेत.
        </span>
      </label>
      {!doc.loading && !scrolledToEnd && (
        <p className="step-sub" style={{ margin: "8px 0 0" }}>
          चेकबॉक्स सक्रिय करण्यासाठी कृपया संपूर्ण मजकूर खालपर्यंत स्क्रॉल करा.
        </p>
      )}
    </StepCard>
  );
}

function ReviewSummary({
  form,
  onEdit,
  open = false,
}: {
  form: CandidateFormState;
  onEdit: () => void;
  open?: boolean;
}) {
  const t = form.candidate_type;

  const detailsGroup: { heading: string; rows: [string, string][] } =
    t === "school"
      ? {
          heading: "शालेय तपशील",
          rows: [
            ["इयत्ता", gradeLabel(form.school.grade)],
            ["शाळेचे नाव", form.school.school_name],
            ["शाळेचा पत्ता", form.school.school_address],
            ["शाळेचे नगर", form.school.school_locality],
            ["शाळेचे गाव / शहर", form.school.school_city],
            ["शाळेचा जिल्हा", districtLabel(form.school.school_district)],
            ["शाळेचा पिनकोड", form.school.school_pincode],
            ["शाळेचा दूरध्वनी क्रमांक", form.school.school_phone],
            ["शाळेचा ईमेल", form.school.school_email],
          ],
        }
      : t === "college"
        ? {
            heading: "महाविद्यालयीन तपशील",
            rows: [
              ["स्तर", collegeLevelLabel(form.college.level)],
              ["अभ्यासाचे वर्ष", form.college.year_of_study],
              ["पदवी / अभ्यासक्रमाचे नाव", form.college.degree],
              ["महाविद्यालय / संस्थेचे नाव", form.college.institution_name],
            ],
          }
        : {
            heading: "नोकरी / व्यवसाय तपशील",
            rows: [
              ["शैक्षणिक पात्रता", qualificationLabel(form.open.qualification)],
              ["व्यवसाय", form.open.profession],
            ],
          };

  const groups: { heading: string; rows: [string, string][] }[] = [
    {
      heading: "मूलभूत माहिती",
      rows: [
        ["पूर्ण नाव", form.name],
        ["जन्मतारीख", formatDateMr(form.dob)],
        ["लिंग", genderLabel(form.gender)],
        ["स्पर्धेसाठी निवडलेली भाषा", languageLabel(form.language)],
      ],
    },
    {
      heading: "संपर्क तपशील",
      rows: [
        ["मोबाईल क्रमांक", normalizeMobile(form.phone)],
        [
          "व्हॉट्सअ‍ॅप क्रमांक",
          form.whatsapp_phone.trim()
            ? normalizeMobile(form.whatsapp_phone)
            : "मोबाईल क्रमांकाप्रमाणेच",
        ],
        ["ईमेल पत्ता", form.email.trim()],
      ],
    },
    {
      heading: "पत्ता",
      rows: [
        ["संपूर्ण पत्ता", form.address],
        ["गाव / शहर", form.city],
        ["नगर", form.locality],
        ["जिल्हा", districtLabel(form.district)],
        ["राज्य", stateLabel(form.state)],
        ["पिनकोड", form.pincode],
      ],
    },
    { heading: "गट", rows: [["गट", typeLabel(t)]] },
    detailsGroup,
  ];

  return (
    <details className="review" open={open}>
      <summary>
        <span>भरलेली माहिती तपासा</span>
        <Icon name="chevronDown" size={18} />
      </summary>
      <div className="review-scroll">
        <dl className="review-list">
          {groups.map((g) => (
            <div
              key={g.heading}
              className="review-list__group"
              role="group"
              aria-label={g.heading}
            >
              <div className="review-list__heading">{g.heading}</div>
              {g.rows.map(([k, v]) => (
                <div key={k} className="review-list__row">
                  <dt>{k}</dt>
                  <dd>{v || "—"}</dd>
                </div>
              ))}
            </div>
          ))}
        </dl>
      </div>
      <button type="button" className="link-btn" onClick={onEdit}>
        माहिती दुरुस्त करा
      </button>
    </details>
  );
}

function Success({
  id,
  at,
  name,
  type,
  onAnother,
}: {
  id: string;
  at: Date;
  name: string;
  type: CandidateType;
  onAnother: () => void;
}) {
  return (
    <section className="success-panel">
      <div className="success-icon" aria-hidden="true">
        <Icon name="check" size={40} />
      </div>
      <h2>नोंदणी यशस्वी झाली!</h2>
      <p>
        {name}, आपली नोंदणी यशस्वीरित्या पूर्ण झाली आहे. कृपया खालील आयडी जपून
        ठेवा — पुढील सर्व संपर्कासाठी तो आवश्यक असेल.
      </p>

      <CandidateIdBox id={id} />

      <dl className="success-meta">
        <div>
          <dt>गट</dt>
          <dd>{typeLabel(type)}</dd>
        </div>
        <div>
          <dt>नोंदणी दिनांक</dt>
          <dd>{formatDateMr(at.toISOString())}</dd>
        </div>
      </dl>

      <p className="step-sub">
        या पानाचा स्क्रीनशॉट घ्या किंवा आयडी लिहून ठेवा. फेरीचे वेळापत्रक व
        सूचना आपल्या नोंदणीकृत मोबाईल क्रमांकावर कळवल्या जातील.
      </p>

      <div className="success-actions">
        <Link to="/" className="btn btn-primary btn-md">
          मुखपृष्ठावर जा
        </Link>
        <Button variant="secondary" onClick={onAnother}>
          दुसऱ्या स्पर्धकाची नोंदणी करा
        </Button>
      </div>
    </section>
  );
}

/* ------------------------ 429: too many attempts ------------------------- */

function useSecondsLeft(until: number) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return Math.max(0, Math.ceil((until - now) / 1000));
}

const clock = (sec: number) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mmss = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return toDeva(h ? `${h}:${mmss}` : mmss);
};

function RateLimited({
  until,
  onRetry,
}: {
  until: number;
  onRetry: () => void;
}) {
  const left = useSecondsLeft(until);
  const ready = left === 0;
  const at = new Intl.DateTimeFormat("mr-IN", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }).format(new Date(until));

  return (
    <ErrorPage
      code={429}
      title="खूप नोंदणी प्रयत्न झाले"
      aside={
        <div
          className={`wait-box ${ready ? "is-ready" : ""}`}
          role="timer"
          aria-live="off"
        >
          {ready ? (
            <>
              <Icon name="check" size={22} />
              <span>आता पुन्हा प्रयत्न करता येईल.</span>
            </>
          ) : (
            <>
              <span className="wait-box__label">पुन्हा प्रयत्न करता येईल</span>
              <span className="wait-box__clock num">{clock(left)}</span>
              <span className="wait-box__at">सुमारे {toDeva(at)} नंतर</span>
            </>
          )}
        </div>
      }
      actions={
        <>
          <Button size="lg" icon="refresh" onClick={onRetry} disabled={!ready}>
            {ready ? "पुन्हा प्रयत्न करा" : "थोडा वेळ थांबा"}
          </Button>
          <Link to="/" className="btn btn-secondary btn-md">
            मुखपृष्ठावर जा
          </Link>
        </>
      }
    >
      <p>
        या इंटरनेट जोडणीवरून किंवा या मोबाईल क्रमांकासाठी थोड्या वेळात खूप
        नोंदणी प्रयत्न झाले आहेत. सुरक्षेसाठी काही काळ नवीन नोंदणी थांबवली आहे.
      </p>
      <p>
        <strong>आपली भरलेली माहिती जतन केलेली आहे</strong> — पुन्हा प्रयत्न
        करताना ती परत भरावी लागणार नाही.
      </p>
      <ul className="error-page__tips">
        <li>
          एकाच मोबाईल क्रमांकाने वारंवार प्रयत्न करू नका — प्रत्येक स्पर्धकाचा
          स्वतःचा क्रमांक वापरा.
        </li>
        <li>
          शाळेतून एकाच वाय-फायवरून अनेक विद्यार्थ्यांची नोंदणी करत असल्यास, काही
          नोंदणी दुसऱ्या जोडणीवरून (उदा. मोबाईल डेटा) करा.
        </li>
        <li>
          अडचण कायम राहिल्यास <Link to="/about">आयोजकांशी संपर्क</Link> साधा.
        </li>
      </ul>
    </ErrorPage>
  );
}
